import dayjs from 'dayjs'
import { createSignal, onMount, createMemo, For } from 'solid-js'
import { Portal } from 'solid-js/web'
import { SearchOutline } from '@/icons'
import { res2json } from '@/utils'
import account from '@/store/account'

const [rows, setRows] = createSignal([])
const [bettingStatistics, setBettingStatistics] = createSignal({})

export default function Statistics() {
  const playerRows = createMemo(() => rows().filter(e => !e.player.is_robot))
  const robotRows = createMemo(() => rows().filter(e => e.player.is_robot))

  const query = new URLSearchParams(document.location.search)
  const [date, setDate] = createSignal(query.get('date') ?? dayjs().tz('Asia/Chongqing').format('YYYY-MM-DD'))
  const [type, setType] = createSignal(query.get('t') ?? '')
  const [keywords, setKeywords] = createSignal(query.get('k') ?? '')
  const [selected, setSelected] = createSignal([])
  const [loading, setLoading] = createSignal(true)
  const [sending, setSending] = createSignal(false)

  onMount(() => {
    query.set('date', dayjs(date()).startOf('day').unix())
    query.set('k', keywords())

    Promise.allSettled([
      fetch(`/api/statistics?${query.toString()}`).then(res2json).then(r => Promise.all((r.data ?? []).map(e => {
        return fetch(`/api/players/${e.player.code}/statistics?${query.toString()}`).then(res2json).then(r => ({ player: e.player, ...r.data }))
      }))).then(arr => {
        arr.sort((a, b) => {
          if (a.player.is_robot !== b.player.is_robot) {
            return a.player.is_robot === b.player.is_robot ? 0 : a.player.is_robot > b.player.is_robot ? 1 : -1
          }
          return a.fan_valid_bet_amount === b.fan_valid_bet_amount ? 0 : a.fan_valid_bet_amount < b.fan_valid_bet_amount ? 1 : -1
        })
        setRows(arr)
      }),
      fetch(`/api/betting/statistics?${query.toString()}`).then(res2json).then(r => { setBettingStatistics(r.data ?? {}) }),
    ]).finally(() => { setLoading(false) })
  })

  const totalScores = createMemo(() => {
    const r = rows() ?? []
    return {
      bet_amount: r.reduce((a, b) => a + (b.player.is_robot ? 0 : (b.bet_amount + b.fan_bet_amount)), 0),
      fan_valid_bet_amount: r.reduce((a, b) => a + (b.player.is_robot ? 0 : (b.bet_amount + b.fan_valid_bet_amount)), 0),
      deposit: r.reduce((a, b) => a + (b.player.is_robot ? 0 : b.deposit), 0),
      withdrawal: r.reduce((a, b) => a + (b.player.is_robot ? 0 : b.withdrawal), 0),
      profit_and_loss: r.reduce((a, b) => a + (b.player.is_robot ? 0 : b.profit_and_loss), 0)
    }
  })

  const allSelected = createMemo(() => {
    const sl = selected().length
    const rl = playerRows().length
    return sl && sl === rl
  })
  function toggleAllSelection(val) {
    setSelected(val ? playerRows().map(e => e.player.code) : [])
  }
  function toggleSelection(row) {
    setSelected(e => {
      const i = e.indexOf(row.player.code)
      if (i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return [...new Set([...e, row.player.code])]
    })
  }

  async function batchReset() {
    setSending(true)
    return Promise.allSettled(selected().map(code => fetch(`/api/${account().game_code}/players/${code}/wallet/reset`, {
      method: 'POST'
    }))).then(() => {
      document.location.reload()
    })
  }

  function ActionPanel() {
    return (
      <>
        {!selected().length ? (<></>) : (
          <div class="sticky bottom-2 flex items-center gap-4">
            <button type="button" onClick={() => batchReset()} class="btn" disabled={sending()}>清空流水</button>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>积分记录</li>
          </ul>
        </div>
        <h2 class="text-xl">积分记录</h2>
        <form class="flex flex-wrap items-center gap-2">
          <label class="flex items-center gap-1"><span class="flex-none">日期:</span><input type="date" name="date" value={date()} onChange={e => setDate(e.target.value)} min={dayjs().tz('Asia/Chongqing').subtract(8, 'day').format('YYYY-MM-DD')} class="input input-bordered flex-1" /></label>
          {/*<select name="t" value={type()} onChange={e => setType(e.target.value)} class="select select-bordered">
            <option value="">全部类型</option>
            <option value="player">玩家</option>
            <option value="robot">机/托</option>
          </select>*/}
          <div class="join">
            <input type="text" name="k" value={keywords()} onChange={e => setKeywords(e.target.value)} class="input input-bordered join-item" placeholder="用户名或昵称" />
            <button type="submit" class="btn join-item"><span class="w-5"><SearchOutline /></span></button>
          </div>
        </form>
        <div>
          <div>总流水: {((totalScores().bet_amount ?? 0) / 100.).toFixed(2)}, 总有效流水: {((totalScores().fan_valid_bet_amount ?? 0) / 100.).toFixed(2)}, 总上分: {((totalScores().deposit ?? 0) / 100.).toFixed(2)}, 总下分: {((totalScores().withdrawal ?? 0) / 100.).toFixed(2)}, 总盈亏: {((totalScores().profit_and_loss ?? 0) / 100.).toFixed(2)}</div>
          <div>报网成功: {bettingStatistics().success ?? 0}, 报网失败: {bettingStatistics().failed ?? 0}, 无需报网(机/托): {bettingStatistics().robots ?? 0}, 等待报网: {bettingStatistics().pending ?? 0}</div>
        </div>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th class="px-2 lg:px-3"><input type="checkbox" class="checkbox checkbox-sm" checked={allSelected()} onChange={e => toggleAllSelection(e.target.checked)} /></th>
                <th class="px-2 lg:px-3">用户名(昵称)</th>
                <th class="px-2 lg:px-3">类型</th>
                {account().fixed && (<th class="text-right">定位流水</th>)}
                {account().fan && (
                  <>
                    <th class="px-2 lg:px-3 text-right">番摊流水</th>
                    <th class="px-2 lg:px-3 text-right">番摊有效流水</th>
                  </>
                )}
                <th class="px-2 lg:px-3 text-right">盈亏</th>
                <th class="px-2 lg:px-3 text-right">上分金额</th>
                <th class="px-2 lg:px-3 text-right">下分金额</th>
                <th class="px-2 lg:px-3 text-right">创建时间</th>
              </tr>
            </thead>
            <tbody>
              <For each={playerRows()}>
                {(row) => (
                  <tr class="hover:bg-base-200">
                    <td class="px-2 lg:px-3"><input type="checkbox" class="checkbox checkbox-sm" checked={selected().indexOf(row.player.code) > -1} onChange={e => { toggleSelection(row) }} /></td>
                    <td class="px-2 lg:px-3">{row.player.username}({row.player.nickname})</td>
                    <td class="px-2 lg:px-3">玩家</td>
                    {account().fixed && (<td class="text-right">{((row.bet_amount ?? 0) / 100.).toFixed(2)}</td>)}
                    {account().fan && (
                      <>
                        <td class="px-2 lg:px-3 text-right">{((row.fan_bet_amount ?? 0) / 100.).toFixed(2)}</td>
                        <td class="px-2 lg:px-3 text-right">{((row.fan_valid_bet_amount ?? 0) / 100.).toFixed(2)}</td>
                      </>
                    )}
                    <td class="px-2 lg:px-3 text-right">{((row.profit_and_loss ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{((row.deposit ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{((row.withdrawal ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{row.player.created_at ? dayjs(row.player.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                  </tr>
                )}
              </For>
              <For each={robotRows()}>
                {(row) => (
                  <tr class="bg-sky-50 hover:bg-base-200">
                    <td></td>
                    <td class="px-2 lg:px-3">{row.player.username}({row.player.nickname})</td>
                    <td class="px-2 lg:px-3">机/托</td>
                    {account().fixed && (<td class="text-right">{((row.bet_amount ?? 0) / 100.).toFixed(2)}</td>)}
                    {account().fan && (
                      <>
                        <td class="px-2 lg:px-3 text-right">{((row.fan_bet_amount ?? 0) / 100.).toFixed(2)}</td>
                        <td class="px-2 lg:px-3 text-right">{((row.fan_valid_bet_amount ?? 0) / 100.).toFixed(2)}</td>
                      </>
                    )}
                    <td class="px-2 lg:px-3 text-right">{((row.profit_and_loss ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{((row.deposit ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{((row.withdrawal ?? 0) / 100.).toFixed(2)}</td>
                    <td class="px-2 lg:px-3 text-right">{row.player.created_at ? dayjs(row.player.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <ActionPanel />
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}
      </Portal>
    </>
  )
}
